import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Input,
  Label,
  Pagination,
  PaginationItem,
  PaginationLink,
  Row,
  Spinner,
} from "reactstrap";
import Swal from "sweetalert2";

import BreadCrumb from "../../../../Components/Common/BreadCrumb";
import TableContainer from "../../../../Components/Common/TableContainer";
import {
  createEmailSuppression,
  deleteEmailSuppression,
  EmailSuppression,
  getEmailHealth,
  getEmailSuppressions,
  importEmailSuppressions,
} from "../../../../services/superAdmin/adminEmailSuppressionService";
import { PaginationMeta } from "../../../../types/api";

const PER_PAGE = 15;
const FILTER_DEBOUNCE_MS = 300;

const SuperAdminEmailSuppressions = () => {
  const { t, i18n } = useTranslation();

  const [rows, setRows] = useState<EmailSuppression[]>([]);
  const [meta, setMeta] = useState<PaginationMeta | null>(null);
  const [page, setPage] = useState(1);
  const [emailFilter, setEmailFilter] = useState("");
  const [reasonFilter, setReasonFilter] = useState("");
  const [debouncedEmail, setDebouncedEmail] = useState("");
  const [loading, setLoading] = useState(true);
  const [healthLoading, setHealthLoading] = useState(true);
  const [health, setHealth] = useState<{
    sent24h: number;
    bounceRate24h: number | null;
    complaintRate24h: number | null;
    total: number;
  } | null>(null);
  const [manualEmail, setManualEmail] = useState("");
  const [manualReason, setManualReason] = useState("manual");
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);

  useEffect(() => {
    document.title = t("admin.emailSuppressions.pageTitle");
  }, [t, i18n.language]);

  useEffect(() => {
    const timer = setTimeout(
      () => setDebouncedEmail(emailFilter.trim()),
      FILTER_DEBOUNCE_MS
    );
    return () => clearTimeout(timer);
  }, [emailFilter]);

  useEffect(() => {
    setPage(1);
  }, [debouncedEmail, reasonFilter]);

  const formatDateTime = useCallback(
    (value: string) =>
      new Intl.DateTimeFormat(i18n.language, {
        dateStyle: "short",
        timeStyle: "short",
      }).format(new Date(value)),
    [i18n.language]
  );

  const fetchHealth = useCallback(async () => {
    setHealthLoading(true);
    try {
      const response = await getEmailHealth();
      setHealth({
        sent24h: response.data.window_24h.sent,
        bounceRate24h: response.data.window_24h.bounce_rate,
        complaintRate24h: response.data.window_24h.complaint_rate,
        total: response.data.suppressions_total,
      });
    } catch (error: any) {
      toast.error(error?.data || t("admin.emailSuppressions.toast.healthError"));
    } finally {
      setHealthLoading(false);
    }
  }, [t]);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getEmailSuppressions({
        page,
        per_page: PER_PAGE,
        ...(debouncedEmail ? { email: debouncedEmail } : {}),
        ...(reasonFilter ? { reason: reasonFilter } : {}),
      });
      setRows(response.data);
      setMeta(response.meta);
    } catch (error: any) {
      toast.error(error?.data || t("admin.emailSuppressions.toast.loadError"));
    } finally {
      setLoading(false);
    }
  }, [page, debouncedEmail, reasonFilter, t]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  useEffect(() => {
    fetchHealth();
  }, [fetchHealth]);

  const handleCreate = async () => {
    if (!manualEmail.trim()) {
      toast.error(t("admin.emailSuppressions.toast.emailRequired"));
      return;
    }

    setSaving(true);
    try {
      await createEmailSuppression({
        email: manualEmail.trim(),
        reason: manualReason,
      });
      toast.success(t("admin.emailSuppressions.toast.created"));
      setManualEmail("");
      setManualReason("manual");
      await Promise.all([fetchRows(), fetchHealth()]);
    } catch (error: any) {
      toast.error(error?.data || t("admin.emailSuppressions.toast.createError"));
    } finally {
      setSaving(false);
    }
  };

  const handleImport = async (file: File | null) => {
    if (!file) {
      return;
    }

    setImporting(true);
    try {
      await importEmailSuppressions(file);
      toast.success(t("admin.emailSuppressions.toast.importAccepted"));
      setTimeout(() => {
        fetchRows();
        fetchHealth();
      }, 1500);
    } catch (error: any) {
      toast.error(error?.data || t("admin.emailSuppressions.toast.importError"));
    } finally {
      setImporting(false);
    }
  };

  const handleDelete = useCallback(
    async (row: EmailSuppression) => {
      const result = await Swal.fire({
        title: t("admin.emailSuppressions.delete.title"),
        text: t("admin.emailSuppressions.delete.text", { email: row.email }),
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: t("admin.emailSuppressions.delete.confirm"),
        cancelButtonText: t("admin.emailSuppressions.delete.cancel"),
      });

      if (!result.isConfirmed) {
        return;
      }

      try {
        await deleteEmailSuppression(row.id);
        toast.success(t("admin.emailSuppressions.toast.deleted"));
        await Promise.all([fetchRows(), fetchHealth()]);
      } catch (error: any) {
        toast.error(error?.data || t("admin.emailSuppressions.toast.deleteError"));
      }
    },
    [t, fetchRows, fetchHealth]
  );

  const columns = useMemo(
    () => [
      {
        header: t("admin.emailSuppressions.table.email"),
        accessorKey: "email",
        enableColumnFilter: false,
      },
      {
        header: t("admin.emailSuppressions.table.reason"),
        accessorKey: "reason",
        enableColumnFilter: false,
        cell: (cell: any) => (
          <Badge color="soft-secondary" className="fs-12">
            {cell.getValue()}
          </Badge>
        ),
      },
      {
        header: t("admin.emailSuppressions.table.source"),
        accessorKey: "source",
        enableColumnFilter: false,
      },
      {
        header: t("admin.emailSuppressions.table.suppressedAt"),
        accessorKey: "suppressed_at",
        enableColumnFilter: false,
        cell: (cell: any) => formatDateTime(cell.getValue()),
      },
      {
        header: t("admin.emailSuppressions.table.actions"),
        accessorKey: "id",
        enableColumnFilter: false,
        cell: (cell: any) => {
          const row = cell.row.original as EmailSuppression;
          return (
            <Button color="soft-danger" size="sm" onClick={() => handleDelete(row)}>
              <i className="ri-delete-bin-line align-bottom me-1" />
              {t("admin.emailSuppressions.actions.remove")}
            </Button>
          );
        },
      },
    ],
    [t, formatDateTime, handleDelete]
  );

  const totalPages = meta?.last_page ?? 1;
  const formatRate = (rate: number | null) =>
    rate === null ? t("admin.emailSuppressions.common.notAvailable") : `${(rate * 100).toFixed(2)}%`;

  return (
    <React.Fragment>
      <ToastContainer closeButton pauseOnHover />
      <div className="page-content">
        <Container fluid>
          <BreadCrumb
            title={t("admin.emailSuppressions.breadcrumb.title")}
            pageTitle={t("admin.emailSuppressions.breadcrumb.pageTitle")}
          />

          <Row className="mb-3">
            <Col md={3}>
              <Card className="card-animate">
                <CardBody>
                  <p className="text-uppercase fw-medium text-muted mb-0">
                    {t("admin.emailSuppressions.health.suppressions")}
                  </p>
                  <h4 className="fs-22 fw-semibold mb-0">
                    {healthLoading ? <Spinner size="sm" /> : health?.total ?? 0}
                  </h4>
                </CardBody>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="card-animate">
                <CardBody>
                  <p className="text-uppercase fw-medium text-muted mb-0">
                    {t("admin.emailSuppressions.health.sent24h")}
                  </p>
                  <h4 className="fs-22 fw-semibold mb-0">
                    {healthLoading ? <Spinner size="sm" /> : health?.sent24h ?? 0}
                  </h4>
                </CardBody>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="card-animate">
                <CardBody>
                  <p className="text-uppercase fw-medium text-muted mb-0">
                    {t("admin.emailSuppressions.health.bounce24h")}
                  </p>
                  <h4 className="fs-22 fw-semibold mb-0">
                    {healthLoading ? (
                      <Spinner size="sm" />
                    ) : (
                      formatRate(health?.bounceRate24h ?? null)
                    )}
                  </h4>
                </CardBody>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="card-animate">
                <CardBody>
                  <p className="text-uppercase fw-medium text-muted mb-0">
                    {t("admin.emailSuppressions.health.complaint24h")}
                  </p>
                  <h4 className="fs-22 fw-semibold mb-0">
                    {healthLoading ? (
                      <Spinner size="sm" />
                    ) : (
                      formatRate(health?.complaintRate24h ?? null)
                    )}
                  </h4>
                </CardBody>
              </Card>
            </Col>
          </Row>

          <Row className="mb-3 g-3">
            <Col lg={6}>
              <Card>
                <CardHeader>
                  <h5 className="card-title mb-0">
                    {t("admin.emailSuppressions.manual.title")}
                  </h5>
                </CardHeader>
                <CardBody>
                  <Row className="g-2 align-items-end">
                    <Col md={6}>
                      <Label>{t("admin.emailSuppressions.manual.email")}</Label>
                      <Input
                        type="email"
                        value={manualEmail}
                        onChange={(e) => setManualEmail(e.target.value)}
                        placeholder="cliente@exemplo.com"
                      />
                    </Col>
                    <Col md={3}>
                      <Label>{t("admin.emailSuppressions.manual.reason")}</Label>
                      <Input
                        type="select"
                        value={manualReason}
                        onChange={(e) => setManualReason(e.target.value)}
                      >
                        <option value="manual">manual</option>
                        <option value="hard_bounce">hard_bounce</option>
                        <option value="soft_bounce">soft_bounce</option>
                        <option value="complaint">complaint</option>
                        <option value="imported">imported</option>
                      </Input>
                    </Col>
                    <Col md={3}>
                      <Button color="primary" className="w-100" disabled={saving} onClick={handleCreate}>
                        {saving ? <Spinner size="sm" /> : t("admin.emailSuppressions.manual.submit")}
                      </Button>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </Col>
            <Col lg={6}>
              <Card>
                <CardHeader>
                  <h5 className="card-title mb-0">
                    {t("admin.emailSuppressions.import.title")}
                  </h5>
                </CardHeader>
                <CardBody>
                  <p className="text-muted mb-2">
                    {t("admin.emailSuppressions.import.help")}
                  </p>
                  <Input
                    type="file"
                    accept=".csv,text/csv,text/plain"
                    disabled={importing}
                    onChange={(e) => handleImport(e.target.files?.[0] ?? null)}
                  />
                  {importing && (
                    <div className="mt-2">
                      <Spinner size="sm" className="me-2" />
                      {t("admin.emailSuppressions.import.processing")}
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>

          <Card>
            <CardHeader className="border-0">
              <Row className="g-3 align-items-end">
                <Col md={4}>
                  <h4 className="card-title mb-0">
                    {t("admin.emailSuppressions.card.title")}
                  </h4>
                </Col>
                <Col md={4}>
                  <Label className="mb-1">{t("admin.emailSuppressions.filters.email")}</Label>
                  <Input
                    value={emailFilter}
                    onChange={(e) => setEmailFilter(e.target.value)}
                    placeholder={t("admin.emailSuppressions.filters.emailPlaceholder")}
                  />
                </Col>
                <Col md={4}>
                  <Label className="mb-1">{t("admin.emailSuppressions.filters.reason")}</Label>
                  <Input
                    type="select"
                    value={reasonFilter}
                    onChange={(e) => setReasonFilter(e.target.value)}
                  >
                    <option value="">{t("admin.emailSuppressions.filters.reasonAll")}</option>
                    <option value="manual">manual</option>
                    <option value="hard_bounce">hard_bounce</option>
                    <option value="soft_bounce">soft_bounce</option>
                    <option value="complaint">complaint</option>
                    <option value="imported">imported</option>
                  </Input>
                </Col>
              </Row>
            </CardHeader>
            <CardBody>
              {loading ? (
                <div className="text-center py-5">
                  <Spinner color="primary" />
                </div>
              ) : rows.length === 0 ? (
                <p className="text-muted mb-0">{t("admin.emailSuppressions.empty.noData")}</p>
              ) : (
                <>
                  <TableContainer
                    columns={columns}
                    data={rows}
                    isGlobalFilter={false}
                    customPageSize={PER_PAGE}
                    tableClass="table-nowrap align-middle mb-0"
                    theadClass="table-light"
                  />
                  <div className="d-flex justify-content-between align-items-center mt-3">
                    <span className="text-muted">
                      {t("admin.emailSuppressions.pagination.showing", {
                        from: meta?.from ?? 0,
                        to: meta?.to ?? 0,
                        total: meta?.total ?? 0,
                      })}
                    </span>
                    <Pagination className="pagination-separated mb-0">
                      <PaginationItem disabled={page <= 1}>
                        <PaginationLink previous onClick={() => setPage((p) => Math.max(1, p - 1))} />
                      </PaginationItem>
                      <PaginationItem disabled>
                        <PaginationLink>{page}</PaginationLink>
                      </PaginationItem>
                      <PaginationItem disabled={page >= totalPages}>
                        <PaginationLink next onClick={() => setPage((p) => Math.min(totalPages, p + 1))} />
                      </PaginationItem>
                    </Pagination>
                  </div>
                </>
              )}
            </CardBody>
          </Card>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default SuperAdminEmailSuppressions;
